import { usePageSeo } from "@/hooks/usePageSeo";
import { useSite } from "@/context/SiteContext";
import { useLang } from "@/context/LanguageContext";
import { PageHero } from "@/components/common/PageHero";
import { ContactForm } from "@/components/forms/ContactForm";
import { trackEvent } from "@/lib/api";
import { Mail, Phone, MapPin, MessageCircle, Headphones, Megaphone, Scale } from "lucide-react";

export default function Contact() {
  const { L } = useLang();
  const P = L.pages.contact;
  usePageSeo("contact");
  const { settings } = useSite();
  const wa = (settings.whatsapp || "").replace(/[^0-9]/g, "");

  return (
    <>
      <PageHero
        overline={P.overline}
        title={P.title}
        description={P.desc}
        breadcrumbs={[{ label: L.common.home, to: "/" }, { label: P.overline }]}
      />

      <section className="py-16 lg:py-24">
        <div className="container-tight grid gap-10 lg:grid-cols-[1fr_1.5fr]">
          <div className="space-y-4">
            <ContactCard icon={Mail} label="General" value={settings.email} href={settings.email ? `mailto:${settings.email}` : null} onClick={() => trackEvent("email_click")} testid="contact-email-link" />
            <ContactCard icon={Headphones} label="Support" value={settings.support_email} href={settings.support_email ? `mailto:${settings.support_email}` : null} testid="contact-support-link" />
            <ContactCard icon={Megaphone} label="Marketing & Partnerships" value={settings.marketing_email} href={settings.marketing_email ? `mailto:${settings.marketing_email}` : null} testid="contact-marketing-link" />
            <ContactCard icon={Scale} label="Legal" value={settings.legal_email} href={settings.legal_email ? `mailto:${settings.legal_email}` : null} testid="contact-legal-link" />
            <ContactCard icon={Phone} label="Phone" value={settings.phone} href={settings.phone ? `tel:${settings.phone}` : null} onClick={() => trackEvent("phone_click")} testid="contact-phone-link" />
            {wa && <ContactCard icon={MessageCircle} label="WhatsApp" value={settings.whatsapp} href={`https://wa.me/${wa}`} onClick={() => trackEvent("whatsapp_click")} testid="contact-whatsapp-link" />}
            <ContactCard icon={MapPin} label="Address" value={settings.address} />
            <div className="rounded-xl border border-hair bg-canvas p-5 text-xs leading-relaxed text-subtle">
              For complaints, please select "Complaint" in the enquiry form or see our{" "}
              <a href="/legal/complaints-policy" className="font-semibold text-finblue">Complaints Policy</a>.
            </div>
          </div>
          <ContactForm />
        </div>
      </section>
    </>
  );
}

function ContactCard({ icon: Icon, label, value, href, onClick, testid }) {
  const inner = (
    <div className="flex items-center gap-4 rounded-xl border border-hair bg-white p-5 shadow-sm transition-all hover:shadow-md">
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-navy text-gold"><Icon className="h-5 w-5" strokeWidth={1.5} /></span>
      <div><p className="text-xs uppercase tracking-wide text-subtle">{label}</p><p className="font-medium text-ink">{value || "—"}</p></div>
    </div>
  );
  return href ? <a href={href} target="_blank" rel="noreferrer" onClick={onClick} data-testid={testid}>{inner}</a> : inner;
}
